import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Ban, CheckCircle2, XCircle, Receipt, Wallet, Clock, AlertTriangle, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { cancellationsApi } from '../../lib/api';

const fmt = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

export default function AdminCancellationsPage() {
    const [pending, setPending] = useState([]);
    const [refundQueue, setRefundQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reviewItem, setReviewItem] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [p, r] = await Promise.all([
                cancellationsApi.listPending(),
                cancellationsApi.listRefundPending(),
            ]);
            setPending(p.data || []);
            setRefundQueue(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Daten konnten nicht geladen werden');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const onMarkRefundPaid = async (b) => {
        if (!window.confirm(`Erstattung über ${fmt(b.cancellation_refund_amount)} an ${b.participant_name || b.participant_email} als verbucht markieren?`)) return;
        try {
            await cancellationsApi.markRefundPaid(b.id);
            toast.success('Erstattung verbucht');
            reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Verbuchen fehlgeschlagen');
        }
    };

    return (
        <AdminLayout>
            <div className="space-y-6 max-w-6xl">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
                            <Ban className="h-6 w-6" />
                            Stornierungen
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Übersicht aller Storno-Anfragen aus dem Mitglieder-Portal.
                            Genehmigen Sie eine Anfrage, wandert die Buchung in die Stornoliste; offene Erstattungen
                            erscheinen unten zur Verbuchung in der Buchhaltung.
                        </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={reload} disabled={loading} data-testid="cancellations-reload">
                        {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}
                        Aktualisieren
                    </Button>
                </div>

                {/* Offene Anfragen */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Clock className="h-4 w-4 text-amber-600" />
                            Offene Anfragen ({pending.length})
                        </CardTitle>
                        <CardDescription>Mitglied hat im Portal storniert – Geschäftsstelle muss prüfen und entscheiden.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
                        ) : pending.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg" data-testid="cancellations-empty">
                                Keine offenen Storno-Anfragen.
                            </div>
                        ) : (
                            <CancellationsTable rows={pending} onReview={setReviewItem} />
                        )}
                    </CardContent>
                </Card>

                {/* Erstattungs-Queue (Buchhaltung) */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                            <Wallet className="h-4 w-4 text-emerald-600" />
                            Offene Erstattungen ({refundQueue.length})
                        </CardTitle>
                        <CardDescription>Genehmigte Stornierungen, deren Erstattungsbetrag noch überwiesen werden muss.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {refundQueue.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
                                Keine offenen Erstattungen.
                            </div>
                        ) : (
                            <RefundsTable rows={refundQueue} onMarkPaid={onMarkRefundPaid} />
                        )}
                    </CardContent>
                </Card>
            </div>

            <CancellationReviewDialog
                booking={reviewItem}
                onClose={() => setReviewItem(null)}
                onChanged={() => { reload(); setReviewItem(null); }}
            />
        </AdminLayout>
    );
}

function CancellationsTable({ rows, onReview }) {
    return (
        <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-4 py-3 text-left">Mitglied</th>
                        <th className="px-4 py-3 text-left">Seminar</th>
                        <th className="px-4 py-3 text-left">Eingegangen</th>
                        <th className="px-4 py-3 text-right">Gebühr</th>
                        <th className="px-4 py-3 text-right">Erstattung</th>
                        <th className="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {rows.map(b => (
                        <tr key={b.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => onReview(b)} data-testid={`cancel-row-${b.id}`}>
                            <td className="px-4 py-3">
                                <div className="font-medium">{b.participant_name || '—'}</div>
                                <div className="text-xs text-muted-foreground">{b.participant_email}</div>
                            </td>
                            <td className="px-4 py-3">
                                <div className="text-sm">{b.seminar?.title || '—'}</div>
                                <div className="text-xs text-muted-foreground">{b.seminar?.course_number || ''}</div>
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">
                                {b.cancellation_requested_at ? new Date(b.cancellation_requested_at).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                                <div className="font-medium">{fmt(b.cancellation_fee_amount)}</div>
                                <div className="text-xs text-muted-foreground">{b.cancellation_fee_percent}%</div>
                            </td>
                            <td className="px-4 py-3 text-right">
                                {b.cancellation_refund_amount > 0 ? (
                                    <span className="font-bold text-emerald-700">{fmt(b.cancellation_refund_amount)}</span>
                                ) : b.cancellation_open_amount > 0 ? (
                                    <span className="text-amber-700">offen: {fmt(b.cancellation_open_amount)}</span>
                                ) : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right">
                                <Button variant="outline" size="sm">Prüfen</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function RefundsTable({ rows, onMarkPaid }) {
    return (
        <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-4 py-3 text-left">Mitglied</th>
                        <th className="px-4 py-3 text-left">Seminar</th>
                        <th className="px-4 py-3 text-left">IBAN / Inhaber</th>
                        <th className="px-4 py-3 text-right">Erstattung</th>
                        <th className="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {rows.map(b => (
                        <tr key={b.id} data-testid={`refund-row-${b.id}`}>
                            <td className="px-4 py-3">
                                <div className="font-medium">{b.participant_name || '—'}</div>
                                <div className="text-xs text-muted-foreground">{b.participant_email}</div>
                            </td>
                            <td className="px-4 py-3 text-sm">{b.seminar?.title || '—'}</td>
                            <td className="px-4 py-3 text-xs">
                                <div className="font-mono">{b.refund_iban || '—'}</div>
                                <div className="text-muted-foreground">{b.refund_account_holder || ''}</div>
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-700">{fmt(b.cancellation_refund_amount)}</td>
                            <td className="px-4 py-3 text-right">
                                <Button size="sm" onClick={() => onMarkPaid(b)} data-testid={`refund-paid-${b.id}`}>
                                    <Receipt className="h-3.5 w-3.5 mr-1" /> Verbucht
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CancellationReviewDialog({ booking, onClose, onChanged }) {
    const [denyReason, setDenyReason] = useState('');
    const [showDeny, setShowDeny] = useState(false);
    const [loading, setLoading] = useState(false);
    const open = !!booking;

    useEffect(() => {
        if (!booking) { setShowDeny(false); setDenyReason(''); }
    }, [booking]);

    const approve = async () => {
        setLoading(true);
        try { await cancellationsApi.approve(booking.id); toast.success('Stornierung genehmigt'); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Genehmigen'); }
        finally { setLoading(false); }
    };
    const deny = async () => {
        if (!denyReason.trim()) { toast.error('Bitte eine Begründung angeben'); return; }
        setLoading(true);
        try { await cancellationsApi.deny(booking.id, denyReason.trim()); toast.success('Stornierung abgelehnt'); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Ablehnen'); }
        finally { setLoading(false); }
    };

    if (!booking) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="cancellation-review-dialog">
                <DialogHeader>
                    <DialogTitle>Storno-Anfrage prüfen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <div className="rounded-lg border bg-muted/30 p-3">
                        <div className="text-base font-semibold">{booking.participant_name}</div>
                        <div className="text-xs text-muted-foreground">{booking.participant_email}</div>
                        <div className="mt-2 text-sm">
                            <strong>Seminar:</strong> {booking.seminar?.title} ({booking.seminar?.course_number})
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <span className="text-muted-foreground">Storno-Stufe</span>
                        <span className="text-right">{booking.cancellation_tier_label}</span>
                        <span className="text-muted-foreground">Tage vor Beginn</span>
                        <span className="text-right">{booking.cancellation_days_before ?? '—'}</span>
                        <span className="text-muted-foreground">Stornogebühr</span>
                        <span className="text-right font-medium">{fmt(booking.cancellation_fee_amount)} ({booking.cancellation_fee_percent}%)</span>
                        {booking.cancellation_open_amount > 0 && (
                            <>
                                <span className="text-muted-foreground">Offene Forderung</span>
                                <span className="text-right font-bold text-amber-700">{fmt(booking.cancellation_open_amount)}</span>
                            </>
                        )}
                        {booking.cancellation_refund_amount > 0 && (
                            <>
                                <span className="text-muted-foreground">Erstattung</span>
                                <span className="text-right font-bold text-emerald-700">{fmt(booking.cancellation_refund_amount)}</span>
                            </>
                        )}
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Storno-Grund</div>
                        <div className="rounded-md bg-muted/40 p-2 text-sm">
                            {booking.cancellation_reason_key && <div className="font-medium">{booking.cancellation_reason_key}</div>}
                            {booking.cancellation_reason_text && <div className="text-muted-foreground italic">„{booking.cancellation_reason_text}"</div>}
                            {!booking.cancellation_reason_key && !booking.cancellation_reason_text && <span className="text-muted-foreground">—</span>}
                        </div>
                    </div>

                    {booking.cancellation_refund_amount > 0 && (
                        <div className="rounded-lg border bg-slate-50 p-3 text-xs">
                            <div className="font-semibold mb-1.5 flex items-center gap-1.5">
                                <Wallet className="h-3.5 w-3.5" /> Bankverbindung für die Erstattung
                            </div>
                            <div className="space-y-0.5">
                                <div><strong>Inhaber:</strong> {booking.refund_account_holder || '—'}</div>
                                <div className="font-mono"><strong>IBAN:</strong> {booking.refund_iban || '—'}</div>
                                {booking.refund_bic && <div className="font-mono"><strong>BIC:</strong> {booking.refund_bic}</div>}
                            </div>
                        </div>
                    )}

                    {showDeny && (
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold flex items-center gap-1.5">
                                <AlertTriangle className="h-3.5 w-3.5 text-rose-600" /> Begründung der Ablehnung
                            </label>
                            <textarea
                                value={denyReason}
                                onChange={(e) => setDenyReason(e.target.value)}
                                rows={3}
                                placeholder="Wird dem Mitglied per E-Mail mitgeteilt. Pflichtfeld."
                                className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                data-testid="deny-reason-text"
                            />
                        </div>
                    )}
                </div>
                <DialogFooter className="flex justify-between sm:justify-between">
                    {!showDeny ? (
                        <>
                            <Button variant="outline" onClick={() => setShowDeny(true)} data-testid="cancel-deny-toggle">
                                <XCircle className="h-4 w-4 mr-1" /> Ablehnen
                            </Button>
                            <Button onClick={approve} disabled={loading} data-testid="cancel-approve-btn">
                                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                                Genehmigen
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setShowDeny(false)}>Zurück</Button>
                            <Button variant="destructive" onClick={deny} disabled={loading} data-testid="cancel-deny-confirm">
                                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                                Anfrage ablehnen
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
